// External Dependencies
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import Multiselect from 'multiselect-react-dropdown';
import Spinner from 'sushiweb/Spinner/Spinner';

// Internal Dependencies
import { useQueryCall, useMutationCall } from '../../../api/react-client';
import { EmptyState } from '../../commons';
import { InvalidSearchImg } from '../../../images/images';
import Shimmer from 'sushiweb/Shimmer';
// UI Components
import { Textarea } from '../../../UiKit';
import { getHeaderWithTooltip } from '../../../pages/admin-dashboard/admin-action-forms/bulk-fraud-marking/HeaderTooltip';

// multiSelect styles
const UNMARK_FRAUD_ENDPOINT = 'user/unmark_fraud';

const multiSelectStyles = `
                          .option:hover {
                                background-color: #318616 !important;
                           }
                          .searchWrapper {
                                background-color: white !important;
                          }
                         `;

export const UnmarkFraudModal = ({ orderId, closePopup }) => {
  const multiselectEntityTypesRef = useRef();
  const multiselectReasonsRef = useRef();

  // Form fields state
  const [inputOrderId, setInputOrderId] = useState(
    orderId ? String(orderId) : '',
  );
  const [reasonList, setReasonList] = useState([]);
  const [entityTypesList, setEntityTypesList] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [comment, setComment] = useState('');

  const {
    data: fraudParams,
    error: paramsError,
    isLoading: isParamsLoading,
    isError: isParamsError,
  } = useQueryCall(
    {
      onSuccess: (response) => {
        if (!response?.reason_options) {
          toast.error('Invalid fraud parameters received');
          return;
        }
        // Transform primary reasons into correct format for Multiselect
        const reasonsList = response.reason_options?.map((reason) => ({
          id: reason?.value,
          name: reason?.label,
        }));
        setReasonList(reasonsList);

        // Transform entity types into correct format for Multiselect
        const entityOptions = response.entity_types?.map((entity_type) => ({
          id: entity_type?.value,
          name: entity_type?.label,
        }));
        setEntityTypesList(entityOptions);
      },
    },
    UNMARK_FRAUD_ENDPOINT,
    ['getFraudParams'],
  );

  const resetForm = () => {
    setInputOrderId('');
    setEntityTypes([]);
    setReasons([]);
    setComment('');

    // Reset Entity Types Multiselect
    if (
      multiselectEntityTypesRef.current &&
      multiselectEntityTypesRef.current.resetSelectedValues
    ) {
      try {
        multiselectEntityTypesRef.current.resetSelectedValues();
      } catch (e) {}
    }
    // Reset Reasons Multiselect
    if (
      multiselectReasonsRef.current &&
      multiselectReasonsRef.current.resetSelectedValues
    ) {
      try {
        multiselectReasonsRef.current.resetSelectedValues();
      } catch (e) {}
    }
  };

  //Unmark Fraud
  const { mutate: unmarkFraud, isLoading: isMarking } = useMutationCall(
    {
      onSuccess: (data) => {
        if (data) {
          if (
            data.failed_identifiers?.length > 0 ||
            Object.keys(data.failed_actions || {}).length > 0
          ) {
            toast.error(
              'Unmarked successfully, but some entities failed to process.',
            );
          } else {
            toast.success('Unmarked successfully');
          }
        }
        resetForm();
        if (typeof closePopup === 'function') {
          closePopup();
        }
      },
    },
    UNMARK_FRAUD_ENDPOINT,
    [],
    {
      doNotShowSuccessToast: true,
    },
  );

  const handleEntityTypeUpdate = (selectedList) => {
    setEntityTypes(selectedList || []);
  };

  const handleReasonUpdate = (selectedList) => {
    setReasons(selectedList || []);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputOrderId.trim()) {
      toast.error('Please enter an Order ID');
    } else if (!entityTypes?.length) {
      toast.error('Select at least one entity type');
    } else if (!reasons?.length) {
      toast.error('Select at least one reason');
    } else {
      const dataToSubmit = {
        reason_codes: reasons?.map((entity) => entity?.id) || [],
        comment: comment,
        entity_types: entityTypes?.map((entity) => entity?.id) || [],
        order_id: inputOrderId.trim(),
      };
      unmarkFraud(dataToSubmit);
    }
  };

  if (isParamsError) {
    return (
      <div className='tw-w-full'>
        <EmptyState
          img={InvalidSearchImg}
          heading={paramsError?.message || 'Something went wrong!'}
          isPop={true}
        />
      </div>
    );
  }

  if (isParamsLoading) return <Shimmer height='20rem' width={'100%'} />;

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-gap-2'>
      {fraudParams && (
        <form onSubmit={handleSubmit}>
          <div className='tw-flex tw-flex-col tw-gap-10'>
            {/* Order ID Input field */}
            <label htmlFor='orderId'>
              {getHeaderWithTooltip(
                'Order ID',
                'Enter the Order ID you want to unmark',
              )}
              <input
                id='orderId'
                type='text'
                value={inputOrderId}
                onChange={(e) => setInputOrderId(e.target.value)}
                className='tw-w-full tw-px-6 tw-py-6 tw-rounded-lg tw-border-solid tw-border-[1px] tw-border-grey-300 tw-text-base tw-text-grey-900'
                placeholder='Enter Order ID'
                autoComplete='off'
              />
            </label>

            {/* Entity Types Selection Dropdown */}
            {entityTypesList?.length > 0 ? (
              <label htmlFor='entityTypes' css={multiSelectStyles}>
                {getHeaderWithTooltip(
                  'Entity Type',
                  'Select the type of entity you want to unmark',
                )}
                <Multiselect
                  ref={multiselectEntityTypesRef}
                  avoidHighlightFirstOption
                  options={entityTypesList || []}
                  selectedValues={entityTypes}
                  onSelect={handleEntityTypeUpdate}
                  onRemove={handleEntityTypeUpdate}
                  displayValue='name'
                  placeholder='Search entity types...'
                />
              </label>
            ) : null}

            {/* Reasons */}
            <label htmlFor='primaryReasons' css={multiSelectStyles}>
              {getHeaderWithTooltip('Reasons')}
              <Multiselect
                ref={multiselectReasonsRef}
                avoidHighlightFirstOption
                options={reasonList}
                selectedValues={reasons}
                onSelect={handleReasonUpdate}
                onRemove={handleReasonUpdate}
                displayValue='name'
                placeholder='Search reasons...'
              />
            </label>

            {/* Comments Section */}
            <label htmlFor='Comment'>
              {getHeaderWithTooltip(
                'Remarks',
                'Add any extra information explaining why this should be unmarked',
              )}
              <Textarea
                placeholder='Add some remarks'
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                }}
                maxLimit='300'
                showBtn={false}
              />
            </label>
          </div>

          {/* Submit Button */}
          <div
            css={`
              display: flex;
              justify-content: flex-end;
            `}
          >
            <button
              type='submit'
              disabled={isMarking || isParamsLoading}
              className='tw-text-[14px] tw-font-medium tw-bg-[#318616] tw-text-white tw-rounded-[0.6rem] tw-border-none tw-px-12 tw-py-4 tw-min-h-8 tw-leading-none tw-cursor-pointer disabled:tw-cursor-not-allowed disabled:tw-bg-[#cfcfcf]'
            >
              {isMarking ? (
                <div className='tw-w-full tw-justify-center tw-items-center tw-text-white'>
                  <Spinner color={'white'} />
                </div>
              ) : (
                'Unmark'
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// Update PropTypes to accept both string and number
UnmarkFraudModal.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  closePopup: PropTypes.func.isRequired,
};

UnmarkFraudModal.defaultProps = {
  closePopup: () => {},
};
